import React from "react";
import { injectIntl, IntlShape } from "react-intl";
import { useQuery } from "@apollo/client";
import MaterialTable, { Column } from "material-table";
import { styled } from "@material-ui/styles";
import { Theme } from "@material-ui/core";
import { getSkipReasonLabels } from "../pre-season-shared/constants";
import { PRE_SEASON_PROGRESS } from "./PreSeasonProgressTable";
import messages from "./PageTeamPreSeason.intl";

interface IEntry {
  status: "PENDING" | "DONE" | "NOT_DONE";
  reason?: string;
}

interface ISummaryRow {
  playerId: string;
  playerName: string;
  completed: number;
  notDone: number;
  pending: number;
  total: number;
  completionRate: number;
  topReason: string;
}

const CompletionBar = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const CompletionTrack = styled("div")({
  flex: 1,
  height: 8,
  borderRadius: 4,
  background: "rgba(0, 0, 0, 0.08)",
  overflow: "hidden",
  minWidth: 60,
});

const completionColor = (pct: number) => {
  if (pct >= 75) return "#4caf50";
  if (pct >= 40) return "#ffb74d";
  return "#e53935";
};

const mostFrequentReason = (
  intl: IntlShape,
  entries: IEntry[]
): string => {
  const reasonLabels = getSkipReasonLabels(intl);
  const counts: Record<string, number> = {};
  entries
    .filter((e) => e.status === "NOT_DONE" && e.reason)
    .forEach((e) => {
      counts[e.reason as string] = (counts[e.reason as string] || 0) + 1;
    });
  const entriesSorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (entriesSorted.length === 0) {
    return intl.formatMessage(messages.noReasonsYet);
  }
  const [topReason] = entriesSorted[0];
  return reasonLabels[topReason as keyof typeof reasonLabels] || topReason;
};

interface IPreSeasonSummaryTableProps {
  exerciseId: string;
  intl: IntlShape;
}

const PreSeasonSummaryTable = injectIntl<
  Omit<IPreSeasonSummaryTableProps, "intl">
>(({ exerciseId, intl }) => {
  const { data } = useQuery(PRE_SEASON_PROGRESS, {
    variables: { exerciseId },
  });

  const progress = data && data.preSeasonProgress;
  if (!progress) {
    return null;
  }

  const rows: ISummaryRow[] = progress.rows.map((row: any) => {
    const total = row.completedCount + row.notDoneCount + row.pendingCount;
    return {
      playerId: row.player.id,
      playerName: `${row.player.name} ${row.player.surname}`,
      completed: row.completedCount,
      notDone: row.notDoneCount,
      pending: row.pendingCount,
      total,
      completionRate: total > 0 ? Math.round((row.completedCount / total) * 100) : 0,
      topReason: mostFrequentReason(intl, row.entries),
    };
  });

  const columns: Array<Column<ISummaryRow>> = [
    {
      title: intl.formatMessage(messages.columnPlayer),
      field: "playerName",
    },
    {
      title: intl.formatMessage(messages.columnCompleted),
      field: "completed",
      type: "numeric",
      render: (row) => (
        <span style={{ color: "#4caf50", fontWeight: 700 }}>{row.completed}</span>
      ),
    },
    {
      title: intl.formatMessage(messages.columnNotDone),
      field: "notDone",
      type: "numeric",
      render: (row) => (
        <span style={{ color: "#e53935", fontWeight: 700 }}>{row.notDone}</span>
      ),
    },
    {
      title: intl.formatMessage(messages.columnPending),
      field: "pending",
      type: "numeric",
      render: (row) => (
        <span style={{ color: "#9e9e9e", fontWeight: 700 }}>{row.pending}</span>
      ),
    },
    {
      title: intl.formatMessage(messages.columnCompletion),
      field: "completionRate",
      type: "numeric",
      render: (row) => (
        <CompletionBar>
          <CompletionTrack>
            <div
              style={{
                width: `${row.completionRate}%`,
                height: "100%",
                borderRadius: 4,
                background: completionColor(row.completionRate),
              }}
            />
          </CompletionTrack>
          <span style={{ fontWeight: 700, minWidth: 36 }}>
            {row.completionRate}%
          </span>
        </CompletionBar>
      ),
    },
    {
      title: intl.formatMessage(messages.columnTopReason),
      field: "topReason",
      sorting: false,
    },
  ];

  return (
    <MaterialTable
      title={intl.formatMessage(messages.summaryFor, {
        title: progress.exercise.title,
      })}
      columns={columns}
      data={rows}
      options={{
        search: false,
        paging: false,
        padding: "dense",
      }}
    />
  );
});

export default PreSeasonSummaryTable;
